import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import unescapedText from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import H4 from 'UIComponents/elements/headings/H4';
import UIGrid from 'UIComponents/grid/UIGrid';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import UILogo from 'UIComponents/image/UILogo';
import UIMediaObject from 'UIComponents/layout/UIMediaObject';
import UIList from 'UIComponents/list/UIList';
import UILink from 'UIComponents/link/UILink';
import PartnerTypeIcon from 'ecosystem-marketplace-core/components/icons/PartnerTypeIcon';
import * as PartnerTier from 'ecosystem-marketplace-core/constants/solutions/PartnerTier';
import * as PartnerType from 'ecosystem-marketplace-core/constants/solutions/PartnerType';
import * as ProfileTierLabelKey from 'ecosystem-marketplace-core/constants/solutions/ProfileTierLabelKey'; // @ts-expect-error untyped import

import { getProfileTierLabelI18nKey } from 'ecosystem-marketplace-core/utils/i18n';
import { getIsPrerenderUserAgent } from 'ecosystem-marketplace-core/utils/userAgent';
import * as FilterKey from '../constants/search/FilterKey'; // @ts-expect-error untyped import

import { getCanonicalAppContext } from '../selectors/app'; // @ts-expect-error untyped import

import { getIsUngatedForHomepage } from '../selectors/auth'; // @ts-expect-error untyped import

import { getFullLocationUrl } from '../utils/urls';
import { fromFilterStateToLocation } from '../utils/storefrontFilters';
import { clearStorefrontFilters, updateStorefrontFilters } from '../slices/storefrontFiltersSlice';
import { getServices } from '../slices/serviceCatalogSlice';

const renderPartnerTypeIcon = (partnerType, tier) => /*#__PURE__*/_jsx(PartnerTypeIcon, {
  alt: unescapedText(getProfileTierLabelI18nKey(tier, partnerType, ProfileTierLabelKey.TITLE)),
  bordered: true,
  partnerType: partnerType,
  size: "medium",
  tier: tier
});

const StorefrontPartnerTiers = () => {
  const canonicalAppContext = useSelector(getCanonicalAppContext);
  const catalogServices = useSelector(getServices);
  const isPrerender = getIsPrerenderUserAgent();
  const dispatch = useDispatch();
  const isUngatedForHomepage = useSelector(getIsUngatedForHomepage);
  const getTierLinkProps = useCallback(tier => {
    const newFilters = {
      [FilterKey.TIER]: [tier]
    };
    const filtersLocation = fromFilterStateToLocation(newFilters, catalogServices, isUngatedForHomepage);

    if (isPrerender) {
      return {
        href: getFullLocationUrl(filtersLocation, canonicalAppContext)
      };
    } else {
      return {
        onClick: () => {
          dispatch(clearStorefrontFilters({}));
          dispatch(updateStorefrontFilters({
            updates: newFilters
          }));
        }
      };
    }
  }, [catalogServices, isUngatedForHomepage, isPrerender, canonicalAppContext, dispatch]);
  return /*#__PURE__*/_jsxs(UIGrid, {
    className: "align-center",
    children: [/*#__PURE__*/_jsxs(UIGridItem, {
      size: {
        sm: 12,
        md: 6,
        lg: 5,
        xl: 5
      },
      children: [/*#__PURE__*/_jsx(H4, {
        className: "is--heading-3",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ecosystem-marketplace-solutions-ui.storefront.partnerTiers.title"
        })
      }), /*#__PURE__*/_jsx("p", {
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "ecosystem-marketplace-solutions-ui.storefront.partnerTiers.subtitle_jsx",
          options: {
            linkProps: {
              external: true,
              href: 'https://knowledge.hubspot.com/resources/hubspot-solutions-directory-review-guidelines'
            }
          },
          elements: {
            Link: UILink,
            br: 'br'
          }
        })
      })]
    }), /*#__PURE__*/_jsxs(UIGridItem, {
      size: {
        sm: 12,
        md: 6,
        lg: 4,
        xl: 4
      },
      offset: {
        sm: 0,
        md: 0,
        lg: 3,
        xl: 3
      },
      children: [/*#__PURE__*/_jsx(UILogo, {
        alt: unescapedText('ecosystem-marketplace-solutions-ui.storefront.partnerTiers.logoAltText'),
        className: "m-bottom-5",
        name: "hubspot-wordmark",
        width: 210
      }), /*#__PURE__*/_jsx("div", {
        className: "is--heading-6",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ecosystem-marketplace-solutions-ui.storefront.partnerTiers.programListTitle"
        })
      }), /*#__PURE__*/_jsxs(UIList, {
        childClassName: "m-bottom-2",
        children: [/*#__PURE__*/_jsx(UILink, Object.assign({}, getTierLinkProps(PartnerTier.ELITE), {
          children: /*#__PURE__*/_jsx(UIMediaObject, {
            align: "center",
            itemLeft: renderPartnerTypeIcon(PartnerType.PARTNER, PartnerTier.ELITE),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: getProfileTierLabelI18nKey(PartnerTier.ELITE, PartnerType.PARTNER, ProfileTierLabelKey.TITLE)
            })
          })
        })), /*#__PURE__*/_jsx(UILink, Object.assign({}, getTierLinkProps(PartnerTier.DIAMOND), {
          children: /*#__PURE__*/_jsx(UIMediaObject, {
            align: "center",
            itemLeft: renderPartnerTypeIcon(PartnerType.PARTNER, PartnerTier.DIAMOND),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: getProfileTierLabelI18nKey(PartnerTier.DIAMOND, PartnerType.PARTNER, ProfileTierLabelKey.TITLE)
            })
          })
        })), /*#__PURE__*/_jsx(UILink, Object.assign({}, getTierLinkProps(PartnerTier.PLATINUM), {
          children: /*#__PURE__*/_jsx(UIMediaObject, {
            align: "center",
            itemLeft: renderPartnerTypeIcon(PartnerType.PARTNER, PartnerTier.PLATINUM),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: getProfileTierLabelI18nKey(PartnerTier.PLATINUM, PartnerType.PARTNER, ProfileTierLabelKey.TITLE)
            })
          })
        })), /*#__PURE__*/_jsx(UILink, Object.assign({}, getTierLinkProps(PartnerTier.GOLD), {
          children: /*#__PURE__*/_jsx(UIMediaObject, {
            align: "center",
            itemLeft: renderPartnerTypeIcon(PartnerType.PARTNER, PartnerTier.GOLD),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: getProfileTierLabelI18nKey(PartnerTier.GOLD, PartnerType.PARTNER, ProfileTierLabelKey.TITLE)
            })
          })
        })), /*#__PURE__*/_jsx(UIMediaObject, {
          align: "center",
          itemLeft: renderPartnerTypeIcon(PartnerType.PARTNER, PartnerTier.NONE),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: getProfileTierLabelI18nKey(PartnerTier.NONE, PartnerType.PARTNER, ProfileTierLabelKey.TITLE)
          })
        }), /*#__PURE__*/_jsx(UIMediaObject, {
          align: "center",
          itemLeft: renderPartnerTypeIcon(PartnerType.PROVIDER, undefined),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: getProfileTierLabelI18nKey(PartnerTier.NONE, PartnerType.PROVIDER, ProfileTierLabelKey.TITLE)
          })
        })]
      })]
    })]
  });
};

export default StorefrontPartnerTiers;